/* eslint-disable react-hooks/exhaustive-deps */
import Loading from "components/Loading";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaBriefcase } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { fetchIncidentByMobile } from "services/incidentService";
import { IncidentByMobile, WitnessProtectionStatus } from "types";
import { DateView, errorHandler } from "utils/helper";
import PageHeader from "../../components/PageHeader";

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const { t } = useTranslation();
  const getStatusColor = (status: string) => {
    const statusMap: { [key: string]: string } = {
      submitted: "bg-yellow-100 text-yellow-800 border-yellow-200",
      assigned_to_prosecutor: "bg-red-100 text-red-800 border-red-200",
      assigned_to_clerk: "bg-blue-100 text-blue-800 border-blue-200",
      reviewed: "bg-blue-50 text-blue-800 border-blue-200",
      closed: "bg-green-100 text-green-800 border-green-200",
    };
    return statusMap[status] || "bg-gray-100 text-gray-800 border-gray-200";
  };

  return (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(
        status,
      )}`}
    >
      {t(`status_${status}`) || status}
    </span>
  );
};

const IncidentByMobileList: React.FC = () => {
  const { t } = useTranslation();
  const { showTooltip } = useTooltip();
  const { loginData } = useAuth();

  const [activeTab, setActiveTab] = useState(t("all"));
  const [incident, setIncident] = useState<IncidentByMobile[]>([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const fetchData = () => {
    setLoading(true);
    const api = fetchIncidentByMobile();
    api
      .then((data) => {
        setIncident(data);
        setLoading(false);
      })
      .catch((error) => {
        showTooltip(errorHandler(error));
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const searchTerm = search.trim().toLowerCase();
  const filtered = incident.filter((protection) => {
    const complainant_name =
      protection?.complainant_name?.toString()?.toLowerCase() || "";
    const complainant_phone =
      protection?.complainant_phone?.toString()?.toLowerCase() || "";
    const status = protection?.status?.toString()?.toLowerCase() || "";
    const incident_against =
      protection?.incident_against?.toString()?.toLowerCase() || "";

    const matchesText =
      complainant_name.includes(searchTerm) ||
      complainant_phone.includes(searchTerm) ||
      status.includes(searchTerm) ||
      incident_against.includes(searchTerm);

    return matchesText;
  });

  return (
    <div className="max-w-screen-xl ml-1 mr-4 space-y-6">
      <PageHeader
        title={t("incident_by_mobile")}
        icon={FaBriefcase}
        activeTab={activeTab}
        onTabClick={setActiveTab}
        rightContent={<></>}
      />

      <div className="max-w-screen-xl ml-1 mr-4 space-y-6">
        <div className="overflow-hidden bg-white shadow rounded-xl">
          {loading ? (
            <Loading label={t("loading_incident_by_mobile")} />
          ) : (
            <table className="w-full text-sm border-collapse">
              <thead className="text-white bg-black">
                <tr>
                  <th colSpan={7} className="p-4" rounded-t-lg>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-lg font-semibold">
                        <button
                          onClick={() => navigate(-1)}
                          className="text-white hover:text-gray-300"
                          title={t("go_back")}
                        >
                          ◀️
                        </button>
                        <span>{t("incident_by_mobile_list_table")}</span>
                      </div>

                      <div className="relative w-80">
                        <span className="absolute inset-y-0 flex items-center text-gray-500 pointer-events-none left-3">
                          🔍
                        </span>
                        <input
                          type="text"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          placeholder={t("search")}
                          className="w-full py-2 pl-10 pr-4 text-sm text-black placeholder-gray-600 bg-gray-200 rounded-md focus:outline-none"
                        />
                      </div>
                    </div>
                  </th>
                </tr>
                <tr>
                  <th className="p-3 text-left">{t("name")}</th>
                  <th className="p-3 text-left">{t("phone")}</th>
                  <th className="p-3 text-left">
                    {t("reporter_application_no")}
                  </th>
                  <th className="p-3 text-left">{t("date_of_offense")}</th>
                  <th className="p-3 text-left">{t("status")}</th>
                  <th className="p-3 text-left" rounded-tr-lg>
                    {t("detail")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((i, idx) => (
                  <tr
                    key={idx}
                    className={idx % 2 === 0 ? "bg-[#F7F4F1AB]" : "bg-white"}
                  >
                    <td className="p-3 font-[500] font-[Poppins]">
                      {i?.complainant_name}
                    </td>
                    <td className="p-3 font-[500] font-[Poppins]">
                      {i?.complainant_phone}
                    </td>
                    <td className="p-3 font-[500] font-[Poppins]">
                      {i?.report_application_number}
                    </td>
                    <td className="p-3 font-[500] font-[Poppins]">
                      {DateView(i?.date_of_offense)}
                    </td>
                    <td className="p-3 font-[500] font-[inter]">
                      <StatusBadge status={i?.status || ""} />
                    </td>

                    <td className="p-3 font-[500] font-[Poppins] flex flex-row gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          navigate("/incident-by-mobile/" + i?.id + "/");
                        }}
                        className="px-4 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300"
                      >
                        {t("view")}
                      </button>
                    </td>
                  </tr>
                ))}
                {incident.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-4 text-center text-gray-500">
                      {t("no_incident_by_mobile_found")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default IncidentByMobileList;
